/* Homepage-only product photography and progressively loaded 3D viewing. */
if (!customElements.get('dut-product-explorer')) {
  let runtimePromise;
  let runtimeAttempts = 0;

  customElements.define('dut-product-explorer', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.frames = [...this.querySelectorAll('[data-explorer-frame]')];
      this.views = [...this.querySelectorAll('[data-explorer-view]')];
      this.count = this.querySelector('[data-view-count]');
      this.caption = this.querySelector('[data-explorer-caption]');
      this.photoControls = this.querySelector('[data-photo-controls]');
      this.zoom = this.querySelector('[data-explorer-zoom]');
      this.stage = this.querySelector('[data-model-stage]');
      this.controls = this.querySelector('[data-model-controls]');
      this.rotation = this.querySelector('[data-model-rotation]');
      this.status = this.querySelector('[data-model-status]');
      this.launch = this.querySelector('[data-show-model]');
      this.closeModel = this.querySelector('[data-close-model]');
      this.modelToolbar = this.querySelector('[data-model-toolbar]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.currentPhoto = 0;
      this.loading = false;
      this.modelLoaded = false;
      this.preferPhotos = false;
      this.setAttribute('data-ready', '');
      this.removeAttribute('data-model-state');

      this.views.forEach((button, index) => button.addEventListener('click', () => {
        this.preferPhotos = true;
        this.selectPhoto(index);
      }, { signal }));
      this.zoom?.addEventListener('input', () => {
        this.preferPhotos = true;
        this.style.setProperty('--explore-zoom', Number(this.zoom.value) / 100);
        this.querySelector('[data-zoom-output]').textContent = `${this.zoom.value}%`;
      }, { signal });
      if (!this.dataset.modelSrc || !this.stage) return;
      this.showPhotos();
      this.photoControls.addEventListener('focusin', () => { this.preferPhotos = true; }, { signal });

      this.rotation.addEventListener('input', () => {
        const angle = Number(this.rotation.value);
        this.updateRotation(angle);
        this.setOrbit(angle);
      }, { signal });
      this.querySelector('[data-model-reset]').addEventListener('click', () => {
        this.updateRotation(25);
        this.setOrbit(25, this.initialOrbit.radius, this.initialOrbit.phi);
      }, { signal });
      this.querySelectorAll('[data-model-zoom]').forEach((button) => {
        button.addEventListener('click', () => {
          const orbit = this.viewer.getCameraOrbit();
          const factor = button.dataset.modelZoom === 'in' ? 0.86 : 1 / 0.86;
          const radius = Math.min(this.initialOrbit.radius * 1.35, Math.max(this.initialOrbit.radius * 0.72, orbit.radius * factor));
          this.setOrbit(orbit.theta * 180 / Math.PI, radius);
        }, { signal });
      });
      const returnToPhotos = () => {
        this.preferPhotos = true;
        this.showPhotos();
        this.launch.focus({ preventScroll: true });
      };
      this.querySelector('[data-show-photos]').addEventListener('click', returnToPhotos, { signal });
      this.closeModel?.addEventListener('click', returnToPhotos, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.controls.hidden) {
          event.preventDefault();
          returnToPhotos();
        }
      }, { signal });
      this.launch.addEventListener('click', () => {
        this.preferPhotos = false;
        if (this.modelLoaded) {
          this.showModel();
          this.rotation.focus({ preventScroll: true });
        } else {
          this.loadModel();
        }
      }, { signal });
      this.motion.addEventListener('change', () => {
        if (this.viewer) this.viewer.setAttribute('interpolation-decay', this.motion.matches ? '0' : '50');
      }, { signal });

      // Keep the real photography visible until someone chooses the 3D view.
      // The larger model and rendering library are downloaded on that action.
    }

    selectPhoto(index) {
      this.currentPhoto = index;
      this.views.forEach((view, i) => view.setAttribute('aria-pressed', String(i === index)));
      this.frames.forEach((frame, i) => { frame.hidden = i !== index; });
      this.count.textContent = `0${index + 1} / 0${this.views.length} · ${this.views[index].textContent.trim()}`;
    }

    async loadModel() {
      if (this.loading || this.modelLoaded) return;
      this.loading = true;
      this.dataset.modelState = 'loading';
      this.status.textContent = 'Loading 3D preview…';
      this.launch.hidden = document.activeElement !== this.launch;
      this.launch.textContent = 'Loading 3D…';
      this.launch.setAttribute('aria-disabled', 'true');
      const { signal } = this.controller;
      const attempt = this.modelAttempt = (this.modelAttempt || 0) + 1;
      const isCurrent = () => !signal.aborted && attempt === this.modelAttempt;
      this.loadTimeout = setTimeout(() => { if (isCurrent()) this.modelError(attempt); }, 30000);
      try {
        const probe = document.createElement('canvas');
        const context = probe.getContext('webgl2');
        if (!context) throw new Error('3D rendering is unavailable');
        context.getExtension('WEBGL_lose_context')?.loseContext();
        if (!customElements.get('model-viewer')) {
          if (!runtimePromise) {
            const runtimeURL = new URL(this.dataset.modelRuntime, location.href);
            runtimeAttempts += 1;
            // Browsers cache failed module imports by URL; retries need a fresh key.
            if (runtimeAttempts > 1) runtimeURL.searchParams.set('dut_retry', runtimeAttempts);
            runtimePromise = import(runtimeURL.href).catch((error) => {
              runtimePromise = undefined;
              throw error;
            });
          }
          await runtimePromise;
        }
        if (!isCurrent() || !this.loading) return;
        this.stage.replaceChildren(this.querySelector('[data-model-template]').content.cloneNode(true));
        const viewer = this.stage.querySelector('model-viewer');
        this.viewer = viewer;
        this.stage.hidden = false;
        this.stage.setAttribute('aria-hidden', 'true');
        this.stage.inert = true;
        viewer.setAttribute('interpolation-decay', this.motion.matches ? '0' : '50');
        viewer.addEventListener('load', () => {
          if (!isCurrent() || !this.loading) return;
          clearTimeout(this.loadTimeout);
          this.loading = false;
          this.modelLoaded = true;
          this.initialOrbit = viewer.getCameraOrbit();
          this.dataset.modelState = 'ready';
          this.status.textContent = '';
          const restoreFocus = document.activeElement === this.launch;
          this.launch.removeAttribute('aria-disabled');
          this.updateRotation(25);
          if (this.preferPhotos) this.showPhotos();
          else {
            this.showModel();
            if (restoreFocus) this.rotation.focus({ preventScroll: true });
          }
        }, { once: true, signal });
        viewer.addEventListener('error', () => { if (isCurrent()) this.modelError(attempt); }, { signal });
        viewer.addEventListener('camera-change', (event) => {
          if (!isCurrent() || !this.modelLoaded) return;
          const orbit = viewer.getCameraOrbit();
          if (event.detail.source === 'user-interaction') {
            this.updateRotation(((orbit.theta * 180 / Math.PI) % 360 + 360) % 360);
          }
          this.querySelector('[data-model-zoom="in"]').disabled = orbit.radius <= this.initialOrbit.radius * 0.725;
          this.querySelector('[data-model-zoom="out"]').disabled = orbit.radius >= this.initialOrbit.radius * 1.345;
        }, { signal });
        const modelURL = new URL(this.dataset.modelSrc, location.href);
        // model-viewer also caches failed model loads, so do not reuse that entry.
        if (attempt > 1) modelURL.searchParams.set('dut_retry', attempt);
        viewer.src = modelURL.href;
      } catch (error) {
        if (isCurrent()) this.modelError(attempt);
      }
    }

    updateRotation(angle) {
      const degrees = Math.round(angle);
      this.rotation.value = degrees;
      this.rotation.setAttribute('aria-valuetext', `${degrees} degrees`);
      this.querySelector('[data-rotation-output]').textContent = `${degrees}°`;
    }

    async setOrbit(angle, radius, phi) {
      if (!this.modelLoaded) return;
      const viewer = this.viewer;
      const { signal } = this.controller;
      const orbit = viewer.getCameraOrbit();
      viewer.cameraOrbit = `${angle}deg ${phi ?? orbit.phi}rad ${radius ?? orbit.radius}m`;
      await viewer.updateComplete;
      if (signal.aborted || viewer !== this.viewer) return;
      viewer.jumpCameraToGoal();
    }

    showModel() {
      this.dataset.view = 'model';
      if (this.modelToolbar) this.modelToolbar.hidden = false;
      this.stage.hidden = false;
      this.stage.removeAttribute('aria-hidden');
      this.stage.inert = false;
      this.frames.forEach((frame) => { frame.hidden = true; });
      this.photoControls.hidden = true;
      this.count.hidden = true;
      this.controls.hidden = false;
      this.launch.hidden = true;
      this.caption.textContent = 'Explore in 3D';
    }

    showPhotos() {
      this.dataset.view = 'photos';
      if (this.modelToolbar) this.modelToolbar.hidden = true;
      this.stage.hidden = true;
      this.stage.setAttribute('aria-hidden', 'true');
      this.stage.inert = true;
      this.selectPhoto(this.currentPhoto);
      this.photoControls.hidden = false;
      this.count.hidden = false;
      this.controls.hidden = true;
      this.launch.hidden = false;
      this.launch.textContent = this.modelLoaded ? 'Back to 3D' : 'Explore in 3D';
      this.caption.textContent = 'Product photos';
    }

    modelError(attempt) {
      if (this.controller.signal.aborted || attempt !== this.modelAttempt) return;
      this.modelAttempt += 1;
      const restoreFocus = this.stage.contains(document.activeElement) || this.controls.contains(document.activeElement) || this.modelToolbar?.contains(document.activeElement);
      clearTimeout(this.loadTimeout);
      this.loading = false;
      this.modelLoaded = false;
      this.dataset.modelState = 'error';
      this.stage.replaceChildren();
      this.showPhotos();
      this.status.textContent = '3D is unavailable. You can still explore the product photos.';
      this.launch.textContent = 'Try 3D again';
      this.launch.removeAttribute('aria-disabled');
      this.launch.hidden = false;
      if (restoreFocus) this.launch.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      clearTimeout(this.loadTimeout);
      this.modelAttempt = (this.modelAttempt || 0) + 1;
      this.stage?.replaceChildren();
    }
  });
}
